import { AppContext } from "App";

import { getDataReaderClient } from "@multiweb/clients/util";
import { IDataEvent } from "@multiweb/types/contracts";
import { DIContainer } from "Global/Base/DIContainer";
import { ModelDescriptionsManager } from "OW.Remoting/Remoting/Cache/ClassDescriptionCache/ModelDescriptionsManager";
import { IDataEventsManager } from "OW.Remoting/Remoting/IDataEventsManager";
import { ErrorHandler } from "UI/Errors";
import { HeaderTemplate } from "UI/UI.Controls/DataGridSubcontrols/Templates/HeaderTemplate";
import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { AdditionalOrdering, ConditionParams, IOrderBy, OrderType } from "../../../../Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent, StatefulEvent } from "../../../../Global/Base/Events";
import { CancellationTokenSource } from "../../../../Global/Base/Promises/CancellationTokenSource";
import { Deferred, DeferredStateful, PromiseUtils } from "../../../../Global/Base/Promises/PromiseUtils";
import { RightType } from "../../../../Global/Base/Rights/RightTypes";
import { invariant } from "../../../../Global/Base/Utils/AssertUtils";
import { ConditionOperator, StringUtils } from "../../../../Global/Base/Utils/StringUtils";
import { TimerUtils } from "../../../../Global/Base/Utils/TimerUtils";
import { AttrValueReader } from "../../../../OW.Remoting/Remoting/AttrValueReader";
import { BasicDataType, IOWClassDescription } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWObjectList } from "../../../../OW.Remoting/Remoting/DataTypes/OWObjectList";
import { Localization } from "../../../../OW.Remoting/Remoting/Localization";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { IImplicitFormInfo } from "../../../../OW.Remoting/Remoting/OWManagerInterfaces";
import { IBussyAwaitable } from "../../../UI.BaseControls/ControlInterfaces";
import { EventHelper } from "../../../UI.BaseControls/Helpers/EventHelper";
import { IBooleanDetailValueEvent } from "../../../UI.BaseControls/IEvent";
import { ActionType, IAction } from "../../../UI.Controls/Actions/IAction";
import { MenuType } from "../../../UI.Controls/Actions/MenuType";
import { Column } from "../../../UI.Controls/DataGridSubcontrols/Columns/Column";
import { ColumnFactory, IColumnDefinition } from "../../../UI.Controls/DataGridSubcontrols/Columns/ColumnFactory";
import { DataRow } from "../../../UI.Controls/DataGridSubcontrols/Components/DataRow";
import { IDataRow, IDataRowElement, IListDataRowElement } from "../../../UI.Controls/DataGridSubcontrols/Components/IDataRow";
import { EditableDBDataSource, SaveResultData } from "../../../UI.Controls/DataSource/EditableDBDataSource";
import { MSLOVDBDataSource } from "../../../UI.Controls/DataSource/MSLOVDBDataSource";
import { IMultiEditObject } from "../../../UI.Controls/DataSource/MultiEdit/MEOWObject";
import { NPDataSource } from "../../../UI.Controls/DataSource/NPDataSource";
import { DropDownMenuHorizontalOpen } from "../../../UI.Controls/DropDownMenu/DropDownInterfaces";
import { DropDownMenu } from "../../../UI.Controls/DropDownMenu/DropDownMenu";
import { DropDownMenuItem } from "../../../UI.Controls/DropDownMenu/DropDownMenuItem";
import { SeparatorMenuItem } from "../../../UI.Controls/DropDownMenu/SeparatorItem";
import { IDataGrid } from "../../../UI.Controls/IDataGrid";
import { IColumnOrderingChangedEvent, IColumnPositionChangedEvent, IDataGridMultipleSelectionChangedEvent, IDataGridSelectionChangedEvent } from "../../../UI.Controls/IEvent";
import { Control } from "../Controls/Control";
import { FormControlWithLoggerParams, IGenericControl, IInfoProvidingControl } from "../Controls/ControlInterfaces";
import { IFRMParams } from "../Controls/FormParameters";
import { ColumnInfoBuilder } from "../DataBuilders/ColumnInfoBuilder";
import { StringBuilder } from "../DataBuilders/StringBuilder";
import { FormManager } from "../FormManager/IFormManager";
import { FormUICustomProperty, Icon } from "../FormUI/FormUI";
import { MenuUIExtensionProperties } from "../FormUI/FormUIShared";
import { ISystemOperationRights, SystemOperation } from "../Helpers/SystemOperationRights";
import { FrameColumnInfoAttributes, IFrameColumnInfo, IFrameProfileInfo } from "../IFrameColumnInfo";
import { MessageWindow } from "../MessageWindow";
import { UIManager } from "../UIManager";
import { OpenModeType } from "./Actions/DetailFormAction";
import { StandardAction } from "./Actions/StandardAction";
import { IControlInfoObject } from "./ControlInfo";
import { DropDownMenuFilterItem } from "./Filters/DropDownMenuItems/DropDownMenuFilterItem";
import { FilterManager } from "./Filters/FilterManager";
import { IFilterManager } from "./Filters/IFilterManager";
import { IActualObjectChangedEventArgs } from "./Frames";
import { BaseFrame } from "./Frames/BaseFrame";
import {
    EditStyleType,
    IFrameWithFiltering,
    IFrameWithUniqueIAttribute,
    IFulltextSearchableFrame,
    IFulltextSearchCondition,
    IMasterableFrameWithDataGrid,
    IMFNoChangeCheck,
} from "./Frames/FrameInterfaces";
import { FrameSharedMethods, IExportData } from "./Frames/FrameSharedMethods";
import { IEditableFrame } from "./Frames/IEditableFrame";
import { MSLOVCheckedGrid } from "./Frames/MSLOVCheckedGrid";
import { MSLOVGrid } from "./Frames/MSLOVGrid";
import { IListFrame } from "./IFrame";
import { ModalWindowType } from "./ModalWindow";
import { MSLOVRightEvaluationOverrides } from "./MSLOVRightEvaluationOverrides";
import { ObjectCheckingResult, SharedMethods } from "./SharedMethods";
import { Pages } from "./TabPages";

export class MSLOV extends BaseFrame implements IMFNoChangeCheck, IFulltextSearchableFrame, IMasterableFrameWithDataGrid, IEditableFrame, IBussyAwaitable, IListFrame, IInfoProvidingControl, IFrameWithFiltering, IFrameWithUniqueIAttribute {
    public selectionStart: GlobalEvent<MSLOV, any> = new GlobalEvent<MSLOV, any>();
    public selectionEnd: GlobalEvent<MSLOV, any> = new GlobalEvent<MSLOV, any>();
    public override readonly dataGridInitialized: StatefulEvent<MSLOV, IDataGrid<IDataRowElement>> = new StatefulEvent<MSLOV, IDataGrid<IDataRowElement>>();
    public readonly valueChanged: GlobalEvent<MSLOV, any> = new GlobalEvent<MSLOV, any>();
    public readonly editModeChanged: GlobalEvent<IEditableFrame, boolean> = new GlobalEvent<IEditableFrame, boolean>();

    public readonly editStyle: EditStyleType = EditStyleType.esMulti;
    public readonly uniqueIAttrIdentifier: string;
    public formName: string = "";

    private __classDescription: IOWClassDescription | undefined = undefined;

    private _attrName: string;

    public readonly imfNoChangeCheck: boolean = false;
    private _required: boolean = false;
    private _npDataSource: NPDataSource;
    private _defaultSelectionState: boolean = false; // pokud true je pri otevreni automaticky vybrano vse
    private _preselection: boolean = false; // pokud true, budou ve vychozim stavu vybrany hodnoty z objectlistu
    private _conditionParamAttrs: string[] = [];
    private _condition: string;
    private _valueMap: Map<string, { id: string; delete: boolean }> = new Map<string, { id: string; delete: boolean }>();
    private _valueMapCanAddingItem: boolean = true;
    // @ts-ignore // nečteno-ponecháno
    private _classDataChangedListener: any;
    private _ignoreEventCount: number = 0;
    private _ignoreEventAOId: string;
    private readonly _onFormOpenMethod: string;
    private readonly _onFormCloseMethod: string;
    /** Určuje, zda je detail otevírán do nového okna (true) nebo jen modálu (false) */
    private _showOnMainForm: boolean;

    private _dataSource: MSLOVDBDataSource;
    private _dataGrid: MSLOVGrid<IListDataRowElement>;
    private _definitionOrderBy: IOrderBy[];
    public orderBy: IOrderBy[];
    /**
     * Zakáže možnost řazení
     */
    protected readonly _disableSort: boolean = false;

    /**
    * Chain promisů s probihajicimi updaty dat
    */
    private _tasksWaitPromises: Promise<any>[] = [];

    protected override __rightsEvaluationOverrides: MSLOVRightEvaluationOverrides;

    // Filters
    protected _filterManager: FilterManager;
    private _defaultFilterId: string | null;
    private _filterMenu: DropDownMenu;
    private _lastFilterCTS: CancellationTokenSource;
    private _hideFilterRow: boolean = false;
    private _showFilterValues: boolean = false;
    private _filterAction: StandardAction;
    private _filterListAction: StandardAction;

    private _showAllItemsActionVisible: boolean = false;
    private _showOnlyFilterItemsActionVisible: boolean = false;

    protected _headerDropDown: DropDownMenu;
    protected _headerDropDownFilterItem: DropDownMenuFilterItem;
    protected _headerDropDownHc: Column;

    protected _color: string;

    private _showAllItems: StandardAction;
    private _showOnlyFilterItems: StandardAction;
    private _selectItemAction: IAction;
    private _selectNoItemAction: IAction;
    private _refreshOnClick: StandardAction;

    private _queueIdName: string = "_mslov_queue_";
    private static _queueId: number = 0;
    private _validateError: string[] = [];

    /**
     * název třídy, v které budou data MSLov použita
     */
    private _classConditionsOfLOV: string;

    /**
     * název atributu ve třídě classConditionsOfLOV, do kterého budou data vkládána - používá se pro kompetencnčí omezení výběru</remarks>
     */
    private _attrConditionsOfLOV: string;

    /**
     * Instance objektu předávaná detailFormu pro umožnění editace
     */
    private _selectedObjectForDetailForm: OWObject;
    private _detailForm: string;
    protected _detailIsOpened: boolean = false;
    // condition section
    /** Základni condition pro preselect */
    private _preselectionBasicCondition: string = "ID = -1";
    /**
     * Pokud máme základni cond. pro preselect, a na ObjectListu je nastaveno isSelectAll a nejaka zakladni condition,
     * která odpovídala nejakému filtru a kdy bylo vybráno vše, tak jednotlivé objekty v OL by se povazovali za vyrazene z vyberu,
     * proto se ukladaji vybrane objekty do dotatecne podminky, aby se dali sparovat s vychozi podminkou pro preselect
     */
    private _preselectionItemsCondition: string = "";

    private _nopreselectionItemsCondition: string = "";

    /** Obsahuje podminku, ktera slouzi pouze ke zobrazeni dat, neovlivnuje preselectovane polozky */
    private _showPreselectCondition: string = "";
    /** Sestavena podminka pro preselect obsahuje vsechny skutecne vybrane zaznamy (vyjma tech co jsou primo v OL pokud neni isSelectAll) */
    private _buildedPreselectCondition: string = "";

    /** Základní condition pro výběr záznamů, v základu se za ní ještě přidává ještě negace preselection condition */
    private _standardCondition: string = "ID > 0";

    /** Základni filtrovací podmínka */
    private _filterCondition: string = "";

    private _fulltextCondition: IFulltextSearchCondition = {};
    private _fulltextAttributes: string[] = [];

    /**
     * Informace o tom, zda je treba zobrazit položky odpovidajici pouze filtru ci ne
     * Podmínka pro zobrazení všech vybraných záznamu nebo pouze těch, ktere odpovídají filtru
     * - true  = zobrazi pouze vybrané zaznamy odpovidajici filtru
     * - false = zobrazi vsechny vybrané zaznamy
     */
    private _showPreselectOnlyFiltered: boolean = false;

    /** Obsahuje seznam vybrannych poli ktere nezapadji do podminky pro _preselectionItemsCondition */
    private _selectedItemForCond: Set<string> = new Set<string>();
    /** Obsahuje položky, ktere se odeberou s podminky */
    private _removeItemFromCond: Set<string> = new Set<string>();

    private _itemFromOl: Set<string> = new Set<string>();

    private _lastBussyPromise?: Deferred<void>;
    private _initializeActualObject: Promise<void>;
    public isInitExecuteFilter: boolean;
    private _lastQueueTask: Promise<void> = Promise.resolve();
    /** Příznak prvního spuštění metody _queueFnc */
    private _firstQueueRun = true;

    protected _formObjectName: string;
    public get formObjectName(): string {
        return this._formObjectName;
    }
    public get filterManager(): IFilterManager | null {
        return this._filterManager;
    }

    private _columnLoadingFinished: DeferredStateful<boolean> = PromiseUtils.deferStateful<boolean>();

    public override get actualObject(): OWObject {
        return this._actualObject;
    }

    public override set actualObject(value: OWObject) {
        this._initializeActualObject = this._loadActualObject(value);
    }

    public override get masterObject(): OWObject {
        return this.actualObject; // Master object for MSLOV is the same as actual object. Evaluation of visibility rights depends on this.
    }

    public get attrName(): string {
        return this._attrName;
    }

    public set attrName(v: string) {
        this._attrName = v;
    }

    public override get renderStandardRowActions(): boolean {
        return false;
    }

    public set controlValue(_value: OWObjectList) {
        // NPDataSource si data stejne nacita vzdycky sam zvlast
    }

    public get controlValue(): OWObjectList {
        return this._npDataSource.objectList;
    }

    public override get enabled(): boolean {
        return this._enabled;
    }

    public override set enabled(value: boolean) {
        this._enabled = value;
        if (this._dataGrid)
            this._dataGrid.interactive = this.isActive;
    }

    public override get isActive(): boolean {
        return this.enabled && this.interactive;
    }

    public get required(): boolean {
        return this._required;
    }

    public set required(value: boolean) {
        if (this._required === value)
            return;

        this.element.classList.toggle("Required", value);

        this._required = value;
    }

    public override get readVersioned(): boolean | null {
        return this._readVersioned;
    }

    public override set readVersioned(value: boolean) {
        this._readVersioned = value;
    }

    public set headerColor(value: string) {
        this._color = value;
        if (this._dataGrid && this._dataGrid.headerRow)
            this._dataGrid.headerColor = value;
        this.menu.setProperty(FormUICustomProperty.pageColor, value);
    }

    public get headerColor(): string {
        return this._color; //this.dataGrid.Header.Color;
    }
    public get currentDataGrid(): MSLOVGrid<IListDataRowElement> {
        return this._dataGrid;
    }

    /**
     * Vrací atributy, které jsou definici určeny pro fulltextové vyhledávání
     */
    public get fulltextAttributes(): string[] {
        return this._fulltextAttributes;
    }

    /**
     * Podmínka pro fulltextové vyhledávání
     */
    public get fulltextCondition(): IFulltextSearchCondition {
        return this._fulltextCondition;
    }

    public set fulltextCondition(value: IFulltextSearchCondition) {
        this._fulltextCondition = value;
    }

    public override set isEditing(value: boolean) {
        super.isEditing = value;
        this.editModeChanged.trigger(this, value);
    }

    public get isEmpty(): boolean {
        return !this._dataGrid.dataRows.length;
    }

    public get defaultFilterId(): string | null {
        return this._defaultFilterId;
    }

    private _competenceCondition: string | null = null;

    private _autoRefreshInterval: number = 0;

    private async _getCompetenceCondition(): Promise<string> {
        if (this._competenceCondition === null && this._classConditionsOfLOV != null && this._attrConditionsOfLOV != null && !(this.actualObject instanceof IMultiEditObject)) {
            invariant(this.actualObject, "[MSLOV] Cannot get competence condition because actualObject is NULL");
            invariant(this.actualObject.instanceID, "[MSLOV] Cannot get competence condition because value of `instanceId` is null");
            this._competenceCondition = await OWManager.getAttributeLOVConditions(this.actualObject.instanceID, this._classConditionsOfLOV, this._attrConditionsOfLOV, this._getRequestContext());
        }
        return this._competenceCondition;
    }

    public override get profileInfo(): IFrameProfileInfo {
        return this._profileInfo;
    }

    public override set profileInfo(value: IFrameProfileInfo) {
        // Je potřeba počkat na inicializaci datagridu.
        const setProfileInfoFnc = async (): Promise<void> => {
            // Pokud položka neni ještě viditelná, je potřeba nastavit profil až po jejím zviditelnění, jinak nejsou dostupné sloupce a nelze tedy měnit nastavení.
            if (!this.canValueLoad) {
                const valueChangedKey = "mslovValueChanged";
                this._events.addWithKey(valueChangedKey, this.canValueLoadChanged, async (_ex: Control, val: boolean) => {
                    if (val) {
                        this.profileInfo = value;
                        this._events.removeByKey(valueChangedKey);
                    }
                });
                return;
            } else
                await this._columnLoadingFinished.promise;

            if (JSON.stringify(this.profileInfo) !== JSON.stringify(value)) // Kontrola instance profilu. Pokud je stejný, není potřeba mazat.
                await this.clearProfileInfo();

            const ordArr: Array<{ column: Column; profile: IFrameColumnInfo }> = [];
            const keys = Object.keys(value.columns);

            if (value.order && value.order.length > 0) {
                this._dataGrid.setOrderBy(value.order, false);
                this.orderBy = value.order;
            } else {
                if (this._dataGrid.setDefaultOrderBy(this._definitionOrderBy)) // Pokud bylo třídění změněno.
                    this.orderBy = this._dataGrid.orderBy;
            }

            for (const key of keys) {
                const item = value.columns[key];
                const column = this._dataGrid.columns.find((col) => col.attrName === item.attrName);
                ordArr.push({ column: column, profile: item });
            }

            // Pole sloupců gridu setřízené v pořadí, které odpovídá pořadí uloženému v profilu.
            const sortedColumnsArr = this._sortColumnsByOrder(ordArr);

            // Sloupce gridu se přesunou podle jejich pořadí v setřízeném poli.
            for (const [index, column] of sortedColumnsArr.entries()) {
                this._dataGrid.setColumnOrder(column, index + 1);
            }

            // Sloupcům gridu se nastaví šířka.
            for (const item of ordArr) {
                if (!item.profile || !item.column)
                    continue;

                if (item.profile.width !== undefined)
                    this._dataGrid.setColumnWidth(item.column, item.profile.width);
            }

            this._profileInfo = value;

            this.selectedProfileChanged.trigger(this, value);
        };

        this._events.add(this.dataGridInitialized, async () => {
            await setProfileInfoFnc();
        });
    }

    constructor(params: FormControlWithLoggerParams) {
        super(params);

        this._queueIdName += MSLOV._queueId++;
        this.uniqueIAttrIdentifier = this.element.getAttribute("data-I");

        this._detailForm = this.getDataAttr("detailform");
        this._onFormOpenMethod = this.element.getAttribute("data-onformopenmethod");
        this._onFormCloseMethod = this.element.getAttribute("data-onformclosemethod");

        this._attrName = this.element.getAttribute("data-attrname");
        this._condition = this.element.getAttribute("data-condition");
        const condParamsString = this.element.getAttribute("data-conditionparamsstring");
        if (condParamsString)
            this._conditionParamAttrs = condParamsString.split(";");

        this._classConditionsOfLOV = this.element.getAttribute("data-LOVConditionsClass");
        this._attrConditionsOfLOV = this.element.getAttribute("data-LOVConditionsAttr");
        this._showFilterValues = DataConverter.toBoolean(this.element.getAttribute("data-ShowFilterValues"), false);
        this._disableSort = DataConverter.toBoolean(this.element.getAttribute("data-DisableSort"), false);
        this._showOnMainForm = this.getBoolDataAttr("showOnMainForm");
        this._definitionOrderBy = this.orderBy;

        this.objectName = this.element.getAttribute("data-objectname");

        this.fontColorFlags = this.element.getAttribute("data-FontColorFlags");
        this.fontColorAttr = this.element.getAttribute("data-FontColorAttr");

        if (!this.objectName)
            throw Error("Object name is undefined");

        this._allowExport = DataConverter.toBoolean(this.element.getAttribute("data-exportable"), true);
        this._defaultSelectionState = DataConverter.toBoolean(this.element.getAttribute("data-DefaultSelectionState"), false);
        this._preselection = DataConverter.toBoolean(this.element.getAttribute("data-PreSelection"), false);
        this._preselection = this._preselection && !this._defaultSelectionState; // pokud je defaultSelectionState na true preseletion se ignoruje
        this._hideFilterRow = DataConverter.toBoolean(this.element.getAttribute("data-HideFilterRow"), false);
        const fulltextAttributesString = this.getDataAttr("FulltextAttrs");
        if (fulltextAttributesString) {
            this._fulltextAttributes = fulltextAttributesString.split(";");
        }

        this.element.classList.add("MultiSelectLOV", "dataGrid");
        this.element.classList.remove("EditableControl");
        if (this._detailForm)
            this.element.classList.add("datagrid-with-checkbox-and-info");

        // Nastaveni notifikace autorefreshe
        this._autoRefreshInterval = DataConverter.toNumber(this.element.getAttribute("data-autorefresh"));
        if (this._autoRefreshInterval > 0) {
            this._getClassDescription().then((classInfo) => {
                const classNames = classInfo.childClasses.concat(classInfo.relatedClasses, this.objectName);

                const dataEventsManager = DIContainer.instance.create(IDataEventsManager);
                for (const className of classNames) {
                    dataEventsManager.subscribeClassChanges(className, this._classDataChangedListener = (msg: IDataEvent) => {
                        // TODO: (MD, 31. 7. 2018) Docastna funkcionalita, po verzi se to musi na službe upravit, aby to nechodilo vicekrat, ci to bylo identifikovatelné
                        // chodí to dvarkat, protože na službe je to pro každou hvězdičku nová registrace subscribe na session
                        if (this.actualObject && this._ignoreEventAOId == msg.message.id && this._ignoreEventCount-- > 0) {
                            return;
                        }

                        if (this._refreshOnClick && this._refreshOnClick.toolbarButton) {
                            //this._refreshOnClick.toolbarButton.changeVisibilityNotificationDot(true, "GUI-662315");
                            this.menu.setProperty(FormUICustomProperty.notificationDot, {
                                action: ActionType.standardRefreshList,
                                lockey: "GUI-662315",
                                visibility: true,
                            });
                        }
                    });
                }
            });
        }
        this._formObjectName = this.element.getAttribute("data-formobjectname");
    }

    protected async _getClassDescription(): Promise<IOWClassDescription> {
        if (this.__classDescription === undefined)
            this.__classDescription = await ModelDescriptionsManager.instance.getClassInfo(this.objectName);
        return this.__classDescription;
    }

    public override async validate(_checkRecommended?: boolean): Promise<boolean> {
        if (!this.visible) {
            return true;
        }
        this._validateError = [];
        await this.waitForPendingUpdates();

        await this._newEvaluateRights([RightType.required]);

        if (this.required) {
            if (this._showPreselectOnlyFiltered) {
                if (this._itemFromOl.size > 0 || this._npDataSource.isSelectAll)
                    return true;
                else {
                    this._validateError.push(await Localization.getTextPromise("GUI-642081")); // Musíte vybrat nějaký záznam.
                    return false;
                }
            } else {
                const v = this._dataGrid.dataRows.some(row => row.isSelected);
                if (!v)
                    this._validateError.push(await Localization.getTextPromise("GUI-642081")); // Musíte vybrat nějaký záznam.
                return v;
            }
        }

        return true;
    }

    public override async validateAndShowMessage(): Promise<boolean> {
        if (await this.validate())
            return true;
        else {
            const errorText = this._validateError.join(", ");
            await MessageWindow.showAlert(new StringBuilder(errorText));

            return false;
        }
    }

    /**
     * Ceka na dobehnuti zmen dat
     */
    public override waitForPendingUpdates(): Promise<any> {
        return super.waitForPendingUpdates().then(() => Promise.all(this._tasksWaitPromises));
    }

    /**
     * Zaregistruje probihajici zmenu dat
     */
    public addPendingUpdateTask(task: Promise<void>): void {
        this._tasksWaitPromises.push(task);
    }

    /**
     * Znicializuje celý frame. Musí být zavoláno před jeho prvním použitím. Je možné spouštět asynchronní procesy.
     */
    protected override async _initializeVisual(): Promise<void> {
        if (this._initializationStarted) // inicializace může proběhnout jen jednou
            return;

        this._initializationStarted = true;

        /**
         * V této metodě by neměl být žádný výkonný kód.
         * Mělo by zde být pouze volání metod inicializujících jednotlivé oblasti a takovém pořadí, jaké je obecně potřeba
         */

        try {
            UIManager.instance.showLoader();

            // obecná inicializace frame
            await this._initializeFrame();
            // navázání listenerů
            await this._initializeDataSource();
            // vytvoření a nastavení DataGrid
            await this._initializeDataGrid();
            // vytvoření menu
            await this._initializeFormUI();
            // zpracování a zaregistrování akcí do menu
            await this._initializeActions();

            // načtení práv
            await this._initializeRights();

            // inicializace počítající s vyhodnocenými právy
            this._initializeWithRights();

            this._initializeListeners();

            await this._getCompetenceCondition();

            this._isInitialized = true;
            this.initialized.trigger(this, undefined);
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    protected override _createOrGetRightsOverrides(): MSLOVRightEvaluationOverrides {
        if (!this.__rightsEvaluationOverrides)
            this.__rightsEvaluationOverrides = new MSLOVRightEvaluationOverrides(this, {
                showAllItemsActionVisible: () => this._showAllItemsActionVisible,
                showOnlyFilterItemsActionVisible: () => this._showOnlyFilterItemsActionVisible,
            });

        return this.__rightsEvaluationOverrides;
    }

    protected override _initializeListeners(): void {
        // zakazani/povoleni udalosti valueChanged
        this._events.add(this.canValueLoadChanged, (_sender: Control, value: boolean) => {
            this._lastQueueTask.finally(() => this.valueChanged.enabled = value);
        });
        // Vybrani zalozky pri kliku do seznamu
        this._events.addListener(this.element, "click", () => {
            this.menu.setProperty(FormUICustomProperty.selected, true);
            // zruseni vsech seznamu oznacenych jako aktivnich
            const list = Array.from(this.ownerForm.element.querySelectorAll(".ListFrame")) as Array<HTMLElement>;
            for (const el of list) {
                el.classList.remove("Active");
            }
            this.element.classList.add("Active");
        });
    }

    protected override async _initializeActions(): Promise<void> {
        await super._initializeActions();
        await this._addFrameChoiceActions();

        if (!this.hideToolBar) {
            this.addCustomActions();
        }

        if (this.allowExport || this.allowReport) {
            const data: IExportData = {
                frame: this,
                readVersioned: this.readVersioned,
            };

            if (this.allowReport) {
                FrameSharedMethods.createReportActions(data);
            }

            if (this.allowExport) {
                FrameSharedMethods.createExportActions(data);
            }
        }

        FrameSharedMethods.addFulltextSearch(this);
    }

    /**
     * Přidá akce pro frame. Jejich registrace tak nepodlehá nastavení vlastnosti hideToolBar.
    */
    protected async _addFrameChoiceActions(): Promise<void> {
        this.menu.addAction(FrameSharedMethods.createRecordsCountAction(this, this._dataSource));
        await this._addShowSubRecordsAction();
    }

    protected _initializeWithRights(): void {
        if (!this.masterFrame) {
            if (this.ownerForm.detailFrame != null) {
                const inEdit = this.ownerForm.openInEdit || this.ownerForm.openMode === OpenModeType.insert;
                this.ownerForm.setToolbarToEditMode(inEdit);
            }

            if (this.ownerForm.queryMode) {
                this._dataGrid.setToQueryMode(async () => {
                    this.filterManager?.clearFilter();
                    await this.refreshOnClick();
                });

                if (!this._hideFilterRow) {
                    this._showFilterPanel();
                }
            }
        }

        this.interactive = !this.ownerForm.readOnly;

        if (this.detail) {
            this.detail.hasSplitter = this.visible;
        }
    }

    public override addCustomActions(): void {
        super.addCustomActions();

        if (!this._hideFilterRow) {
            this._filterAction = FrameSharedMethods.createFilterAction(ActionType.standardFilter, async () => {
                if (this.ownerForm.filterPanel.isShown(this.uniqueIAttrIdentifier)) {
                    this.ownerForm.filterPanel.hide();
                } else {
                    this._showFilterPanel();
                }
            }, this);
            this.menu.addAction(this._filterAction);
            this._filterAction.enabled = true;

            this._filterListAction = FrameSharedMethods.createFilterAction(ActionType.standardFilterList, async () => {
                if (!this.interactive) {
                    return;
                }
                await this._createFilterBtnMenu(); //pri zavreni se odregistruji udalosti, proto vytvarim znova
                this._filterMenu.show(this._filterListAction.toolbarButton.element, null);
            }, this);
            this.menu.addAction(this._filterListAction);
            this._filterListAction.enabled = true;
        }
    }

    public setDefaultFilterId(filterId: string | null): boolean {
        if (!this.isInitialized) {
            this._defaultFilterId = filterId;
            return true;
        }

        return false;
    }

    /** Zobrazí filtrovací panel */
    private _showFilterPanel(): void {
        this._filterManager.updateFilterPanel();

        this.ownerForm.filterPanel.color = this.headerColor;
        this.ownerForm.filterPanel.show();
    }

    protected async _initializeDataSource(): Promise<void> {
        this._npDataSource = new NPDataSource({
            className: this.objectName,
            condition: this._condition,
            frameContext: this.frameContext,
        });
        this._dataSource = new MSLOVDBDataSource({
            className: this.objectName,
            condition: this._condition,
            readVersioned: this.readVersioned,
            maxRecordCount: undefined,
            frameContext: this.frameContext,
        },
        this._logger,
        );

        const ordering = this.element.getAttribute("data-orderby");

        this.orderBy = [];
        if (ordering != null) {
            this.orderBy = [];

            const objectClass = await this._getClassDescription();

            this.orderBy = FrameSharedMethods.parseOrderBy(ordering);

            if (this.orderBy)
                for (const order of this.orderBy) {
                    const attrType = (await objectClass.getAttributeInfo(order.attributeName)).basicType;
                    if (attrType == BasicDataType.String || attrType == BasicDataType.StrNum)
                        order.additionalOrdering = AdditionalOrdering.linguistic;
                }
        }

        this._dataSource.orderBy = this.orderBy;

        this._events.add(this._npDataSource.valueChanged, (_l, v) => {
            this.valueChanged.trigger(this, v);
        });

        this._dataSource.error.add((_sender, error) => {
            ErrorHandler.instance.handleError(error);
        });
    }

    /**
     * Vybere / zruší vybrání všech záznamů
     * @param isSelect Jestli vybrat nebo zrušit vybrání
     */
    private async _selectAll(isSelect: boolean): Promise<void> {
        await TimerUtils.waitOrCancelPromise(this._queueIdName);
        if (this._initializeActualObject) {
            await this._initializeActualObject;
        }
        this._setBusyState(true);
        this._clearQueue();
        this._dataSource.selectChangeOnData(isSelect, false); // pokud nekdo vybral na pomalejsim stroji a okamzite zacal zbesile scrollovat dochazelo k tomu, ze nektere zaznamy mely puvodni stav
        this._setButtonsConditions(isSelect);
        this._setBusyState(false);
    }

    protected override async _initializeFormUI(): Promise<void> {
        this.menu = this.ownerForm.formUI.createFrameMenu(this);
        this.isMenuOwner = true;
        this.headerColor = Pages.getColor();

        this.dataGridInitialized.add(() => {
            this._dataGrid.registerRowControlsFactory((dataKeyFetcher: () => string) => this.menu.renderRecordControls(dataKeyFetcher));
        });

        if (this.ownerForm != null)
            this._events.add(this.ownerForm.readOnlyChanged, (_sender, e) => this.interactive = !e);

        this.menu.setVisibility(this.canValueLoad);

        this._refreshOnClick = FrameSharedMethods.createStandardAction(ActionType.standardRefreshList, () => this.refreshOnClick(), this);
        this._refreshOnClick.toMenu = MenuType.mtActions;
        this._refreshOnClick.enabled = true;
        this._refreshOnClick.visible = true;
        this.menu.addAction(this._refreshOnClick);

        this._selectItemAction = FrameSharedMethods.createStandardAction(ActionType.standardSelectAll, async () => this._selectAll(true), this);
        this._selectItemAction.toMenu = MenuType.mtActions;
        this._selectItemAction.enabled = true;
        this._selectItemAction.visible = true;
        this.menu.addAction(this._selectItemAction);

        this._selectNoItemAction = FrameSharedMethods.createStandardAction(ActionType.standardDeselectAll, async () => this._selectAll(false), this);
        this._selectNoItemAction.toMenu = MenuType.mtActions;
        this._selectNoItemAction.enabled = true;
        this._selectNoItemAction.visible = true;
        this.menu.addAction(this._selectNoItemAction);

        this._showAllItems = FrameSharedMethods.createStandardAction(ActionType.standardShowAll, async () => {
            this._switchShowFilterItems(false);
        }, this);
        this._showAllItems.toMenu = MenuType.mtActions;
        this._showAllItems.enabled = true;
        this._showAllItems.visible = false;
        this.menu.addAction(this._showAllItems);

        this._showOnlyFilterItems = FrameSharedMethods.createStandardAction(ActionType.standardShowFiltered, async () => {
            this._switchShowFilterItems(true);
        }, this);
        this._showOnlyFilterItems.toMenu = MenuType.mtActions;
        this._showOnlyFilterItems.enabled = true;
        this._showOnlyFilterItems.visible = false;
        this.menu.addAction(this._showOnlyFilterItems);

        if (this._detailForm) {
            const openDetailAction = FrameSharedMethods.createStandardRecordAction(ActionType.standardOpenDetail, async (e: Event, dataKey: string) => {
                EventHelper.stopPropagation(e);

                if (this._dataGrid != undefined)
                    this._dataGrid.lastClickedRow = this._dataGrid.dataRows.find((row) => row.dataKey == dataKey);
                return this._openDetailSeparately(dataKey);
            }, this);
            openDetailAction.enabled = true;
            openDetailAction.visible = true;
            this.menu.addAction(openDetailAction);
        }

        if (this.menu && this._fulltextAttributes.length > 0)
            this.menu.setProperty(MenuUIExtensionProperties.fulltextSupported, true);

        this.menu.setVisibility(this.canValueLoad);

        // Popisek seznamu
        this._createFrameCaption();
    }

    protected async _initializeDataGrid(): Promise<void> {
        this._dataGrid = new MSLOVCheckedGrid();
        this._dataGrid.showFrameMenu = true;

        if (this._dataGrid.emptyMessage) {
            this._dataGrid.emptyMessage.emptyMessageCustomization = this.emptyMessageCustomization;
        }

        this._dataGrid.dataSource = this._dataSource;
        this._dataGrid.orderBy = this.orderBy;
        this._dataGrid.headerRow.visible = true;
        this._dataGrid.setExtendedProperties(this._getExtendedProperties());
        if (this.fontColorAttr) this._dataGrid.flagAttributeName = this.fontColorAttr;

        this._dataGrid.objectName = this.objectName;

        if (this._disableSort)
            this._dataGrid.disableSort();

        this._events.addListener(this._dataGrid, "SelectionChanged", (e: IDataGridSelectionChangedEvent) => {
            this._onSelectionChanged(e);
        });
        this._events.addListener(this._dataGrid, "MultipleSelectionChanged", (e: IDataGridMultipleSelectionChangedEvent) => {
            this._onMultipleSelectionChanged(e);
        });

        this._dataGrid.addEventListener("resizablestop", (e) => {
            const headerTemplate = e.target as HeaderTemplate;
            const width = headerTemplate.parentColumn.width;
            this._setProfileColumnInfoAttr(headerTemplate.parentColumn.attrName, FrameColumnInfoAttributes.width, width);
            this.frameProfileChanged.trigger(this, null);
        });

        if (this._disableSort)
            this._dataGrid.disableSort();
        else
            this._events.addListener(this._dataGrid, "OrderingChanged", (e: IColumnOrderingChangedEvent) => {
                this.orderBy = e.detail.order;
                this.profileInfo.order = e.detail.order;
                this.frameProfileChanged.trigger(this, null);
            });

        this._events.addListener(this._dataGrid, "ColumnPositionChanged", (e: IColumnPositionChangedEvent) => {
            // newIndex z události počítá pouze s viditelnými slopuci, ale v profilu potřebujeme hodnotu vztaženou ke všem
            const newOrder = this.currentDataGrid.columns.findIndex(column => column.attrName === e.detail.column.attrName) + 1;

            this._changeProfileColumnOrder(e.detail.column.attrName, newOrder, e.detail.oldPosition);
        });

        this.dataGridInitialized.trigger(this, this._dataGrid);

        const loadedDG = async () => {
            await this._createColumns(FrameSharedMethods.getColumnsDefinition(this.element, "[data-tscitem$='Attribute']"));
            if (!this.attrName)
                this._logger.error("Attrname is empty on MSLOV");
            const fullLengthAttributes = this._dataGrid.columns.filter(column => column.trimValue === false).map(column => column.attrName);
            this._npDataSource.setAttrs(this._dataGrid.columns.map((item) => item.attrName), fullLengthAttributes);
            this._columnLoadingFinished.resolve(true);
        };
        try {
            if (this._dataGrid.canValueLoad) {
                await loadedDG();
            } else {
                this._events.addListener(this._dataGrid, "canvalueload", async (e: IBooleanDetailValueEvent) => {
                    if (!this._dataGrid.initialized && e.detail.value) {
                        await loadedDG();
                    }
                });
            }
        } catch (err) {
            ErrorHandler.instance.handleError(err);
        }

        this.container.appendChild(this._dataGrid);

        this._events.addListener(this._dataGrid, "SelectionAll", async () => {
            this._setBusyState(true);
            this._clearQueue();

            await this._setButtonsConditions(true);
            this._setBusyState(false);
        });

        this._events.addListener(this._dataGrid, "DeselectionAll", async () => {
            this._setBusyState(true);
            this._clearQueue();

            await this._setButtonsConditions(false, MSLOVConditionApply.onlySetCondition);

            this._setBusyState(false);
        });

        await this._loadFilters();
        this._createDesktopDropDownMenu();

        this._events.add(this.canValueLoadChanged, async (_s: Control, val: boolean) => {
            this.menu.setVisibility(val && this.visible);
        });
    }

    /**
     * Nastaví popisek filtru na tlačítko filtrů
     * @param caption Požadovaný popisek
     */
    private _setFilterToolbarCaption(caption: string = null): void {
        if (this._filterListAction && this._filterListAction.toolbarButton)
            this._filterListAction.toolbarButton.setCaption(caption);
    }

    /**
    * Vytvoří pole o velikosti všech sloupců gridu, kde budou všechny sloupce gridu seřazené tak, aby respektovaly 'order' sloupců v profilech
    * Nejdříve se do pole vloží sloupce, jejichž 'order' je uložený v profilech a to právě na pozice, ve kterých se mají nacházet po přesunutí
    * Ostatní pozice pole zůstavají prázdné
    * Ze všech sloupců se vyfiltrují ty sloupce, které nemají v profilech uložený 'order'
    * Tyto sloupce se postupně zařadí na volná místa pole
    */
    private _sortColumnsByOrder(ordArr: { column: Column; profile: IFrameColumnInfo }[]): Column[] {
        const sortedArr = new Array<Column>(this._dataGrid.columns.length);
        for (const item of ordArr) {
            sortedArr[item.profile.order - 1] = item.column;
        }

        // vyfiltruje všechny sloupce kromě těch, co jsou už seřazené
        const unorderedArr = this._dataGrid.columns.filter(col => !sortedArr.includes(col));

        // Na volná místa setřízeného pole se postupně zařadí sloupce, které nemají v profilu uvedený 'order'
        for (const [index, column] of sortedArr.entries()) {
            if (column == null) {
                sortedArr[index] = unorderedArr.shift();
                sortedArr[index].order = index + 1;
            }
        }
        return sortedArr;
    }

    protected _setProfileColumnInfoAttr(attrName: string, propertyName: FrameColumnInfoAttributes, value: any, updateColumnPanel: boolean = true): void {
        let val = this.profileInfo.columns[attrName];
        if (propertyName === FrameColumnInfoAttributes.width && value < Column.MIN_COLUMN_WIDTH)
            value = Column.MIN_COLUMN_WIDTH;
        if (val == null) {
            val = {
                attrName: attrName,
            };
            val[FrameColumnInfoAttributes[propertyName]] = value;
            this.profileInfo.columns[attrName] = val;
        } else {
            val[FrameColumnInfoAttributes[propertyName]] = value;
        }

        if (updateColumnPanel && propertyName !== FrameColumnInfoAttributes.width && this.ownerForm.formSettingsPanel.visible && this.ownerForm.formSettingsPanel.contentBuilder instanceof ColumnInfoBuilder) {
            (this.ownerForm.formSettingsPanel.contentBuilder as ColumnInfoBuilder).reloadData(this._dataGrid.columns, this.profileInfo);
        }
    }

    /**
     * Upraví pozici sloupce v profilu včetně vyvolání události informující o změně profilu
     * @param attrName Přemístěný attribut
     * @param newOrder Nová pozice
     * @param oldOrder Původní pozice
     */
    protected _changeProfileColumnOrder(attrName: string, newOrder: number, oldOrder?: number, updateColumnPanel: boolean = true): void {
        this._setProfileColumnInfoAttr(attrName, FrameColumnInfoAttributes.order, newOrder, updateColumnPanel);

        this._updateProfileColumnsOrder(newOrder, attrName, oldOrder, (oldOrder >= newOrder));

        this.frameProfileChanged.trigger(this, null);
    }

    /**
     * Provede aktualizaci pořadí všech sloupců v profilu
     *
     * @param orderValue pořadí ze kterého se výchazí tedy zvětšuji se položky, které jsou stejné nebo si rovny
     * @param skipAttrName atribut, ktery se preskoci a netyka se ho uprava poradi
     * @param originOrderValue původni číslo řazení, pokud je vyplnění různě od -1, provádí se uprava do tohoto cisla, dal se již nezvešují a to proto, že pokud mění pořadi třeba ze 5 na 3, je treba ovlivni element mezi 3. az 5. pozici, ale nemeli by se upravovat elementy dal
     * @param isAdd Zda má dojít k přičtení 1 k pořadí sloupců (posun směrem doprava). Jinak se odečítá 1 (posun doleva)
     */
    protected _updateProfileColumnsOrder(orderValue: number, skipAttrName: string, originOrderValue: number = -1, isAdd: boolean = true): void {
        const keys = Object.keys(this.profileInfo.columns);

        const min = Math.min(orderValue, originOrderValue);
        const max = Math.max(orderValue, originOrderValue);

        const checkInRange = (ord: number) => {
            if (originOrderValue === -1) {
                return ord >= orderValue;
            }
            return ord >= min && ord <= max;
        };

        for (const key of keys) {
            if (key === skipAttrName)
                continue;

            const col = this.profileInfo.columns[key];

            if (col.order && checkInRange(col.order)) {
                if (col.frozen)
                    continue;

                if (isAdd)
                    col.order++;
                else
                    col.order--;
            }
        }
    }

    /**
     * Method initiated by clicking on the column header to change the ordering of the column or by selecting the ordering in the drop-down menu.
     * @param configuration Configuration object
     */
    protected async _changeOrdering(configuration: {event?: CustomEvent; column?: Column; newOrdering?: IOrderBy[]}): Promise<void> {
        UIManager.instance.showLoader();
        await this._executeQueue(0);
        if (configuration.event) {
            // Click on the column header
            const objectClass = await this._getClassDescription();
            const attrType = (await objectClass.getAttributeInfo(configuration.column.attrName)).basicType;
            const detail = configuration.event.detail;
            await this._dataGrid.changeOrdering(configuration.column, detail.state === 1 ? OrderType.ascending : detail.state === 2 ? OrderType.descending : null, detail.shiftKey,
                attrType === BasicDataType.String || attrType === BasicDataType.StrNum ? AdditionalOrdering.linguistic : null);
        } else {
            // Selecting the ordering in the drop-down menu
            this._dataGrid.setOrderBy(configuration.newOrdering);
        }
        UIManager.instance.hideLoader();
    }

    /**
     * Otevře detail do nového okna nebo modálu
     * @param objID ID otevíraného objektu
     */
    protected async _openDetailSeparately(objID: string = null): Promise<void> {
        if (this._detailIsOpened)
            return;

        UIManager.instance.showLoader();
        let formInfo: IImplicitFormInfo = undefined;
        let actualObject = this.actualObject;
        try {
            let actualObjectGetted = false;
            if (objID && (!actualObject || actualObject.id != objID)) { // otevírání detailu jiného záznamu, než je vybraný - provede se zmena vyberu radku
                actualObject = await this._dataSource.getObject(objID, await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false));
                actualObjectGetted = true;
            }

            try {
                formInfo = await FrameSharedMethods.getDefaultFormName(this._detailForm, this.ownerForm, actualObject, this.frameContext, this._formObjectName);
            } catch (e) {
                ErrorHandler.instance.handleError(e);
                return;
            }

            const dataSource = this._dataSource;
            if (dataSource instanceof EditableDBDataSource)
                this._selectedObjectForDetailForm = !actualObjectGetted ? (await dataSource.getObject(objID, await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false), true)) : actualObject;
        } finally {
            UIManager.instance.hideLoader();
        }

        const handler = {
            get: (target: any, name: string) => {
                return name === "actualObject" ? this._selectedObjectForDetailForm : target[name];
            },
        };
        const replacingMslov: MSLOV = new Proxy(this, handler);

        if (!this._selectedObjectForDetailForm)
            throw new Error("[MSLOV] Cannot get object for detail form!");

        if (formInfo && formInfo.MasterAttr) {
            await OWManager.publishInstance(this._selectedObjectForDetailForm.instanceID, formInfo.MasterAttr, this._getRequestContext());
            this._selectedObjectForDetailForm = AttrValueReader.read(await this._selectedObjectForDetailForm.getValue(formInfo.MasterAttr));
        }

        this._ignoreEventAOId = this._selectedObjectForDetailForm.id;
        this._events.add(this._selectedObjectForDetailForm.beforeSave, () => {
            this._ignoreEventCount = 2;
        });

        try {
            if (this._onFormOpenMethod) {
                try {
                    UIManager.instance.showLoader();
                    await this._selectedObjectForDetailForm.run({
                        methodName: this._onFormOpenMethod,
                        requestContext: this._getRequestContext(),
                    });
                } catch (err) {
                    ErrorHandler.instance.handleError(err);
                    return;
                }
            }
        } finally {
            UIManager.instance.hideLoader();
        }

        let masterLinkToAttr = formInfo && formInfo.DetailAssocName;
        let masterObject = this._selectedObjectForDetailForm;
        let detailFormObject: OWObject;
        if (this._formObjectName) {
            const detailObject = await actualObject?.getValue(this._formObjectName);
            if (detailObject instanceof OWObject) {
                detailFormObject = detailObject;
                masterLinkToAttr = this._formObjectName;
                masterObject = actualObject;
            }
        }

        const params: IFRMParams = {
            applyLOVCondition: true,
            autoSelectFirstRow: true,
            detailFormMasterFrame: replacingMslov,
            detailFormObject: detailFormObject,
            masterLinkToAttr: masterLinkToAttr,
            masterObject: masterObject,
            hideWindowActions: true,
            hideButtons: { insert: true, copy: true, delete: true, prev: true, next: true },
            isImplicit: FrameSharedMethods.isFormNameImplicit(this._detailForm),
            formContext: this.ownerForm.formContext,
        };

        if (this._showOnMainForm) {
            FormManager.instance.openForm(formInfo.FormName, params);
        } else {
            FormManager.instance.openFormModal({
                formName: formInfo.FormName,
                parametricData: params,
                onClose: () => {
                    this._onModalClose();
                    this._detailIsOpened = false;
                },
                type: ModalWindowType.parametricForm,
            });

            this._detailIsOpened = true;
        }
    }

    private async _onModalClose(): Promise<void> {
        if (this._onFormCloseMethod != undefined && this._selectedObjectForDetailForm != undefined) {
            try {
                UIManager.instance.showLoader();
                await this._selectedObjectForDetailForm.run({
                    methodName: this._onFormCloseMethod,
                    requestContext: this._getRequestContext(),
                });
            } finally {
                UIManager.instance.hideLoader();
            }
        }
    }

    protected override _setActualObject(object: OWObject, isFromMasterObjChanged: boolean = false, _removeInstance: boolean = true): void {
        if (this._autoRefreshInterval && this.menu) {
            this.menu.setProperty(FormUICustomProperty.notificationDot, {
                action: ActionType.standardRefreshList,
                visibility: false,
            });
        }

        if (this._competenceCondition) {
            this._competenceCondition = null;
        }

        this._itemFromOl.clear();
        const previousActualObject = this.actualObject;
        super._setActualObject(object, isFromMasterObjChanged, false); //TODO [PB] Dočasný fix, [WI 17339] TC2 to opravuje, ale občas vyskočí padavka "instance not found" při přepínání detailů na mslov.
        this.triggerActualObjectChange(this.actualObject, previousActualObject);
        if (this.actualObject)
            this._resolveActualObjectSetDeferred(true);
    }

    private async _loadActualObject(value: OWObject): Promise<void> {
        if (value != null) {
            if (this._actualObject === value)
                return;

            await this._setInitConditionSetting();
            this._setActualObject(value);

            const objList = (await this._actualObject.getValue(this.attrName) as OWObjectList);
            await this._loadObjectList(objList);
            await this._loadPreselectionSetting();

            if (this._defaultSelectionState) {
                //this._NPDataSource.clearObjectList();
                this._dataGrid.selectAllRows();
            }

            this._events.add(this._actualObject.attributesChanged, async (_sender: OWObject, attributeNames: Array<string>) => {
                if (this._competenceCondition) {
                    this._competenceCondition = null;
                }

                if (this._conditionParamAttrs && this._conditionParamAttrs.length > 0 &&
                    this._conditionParamAttrs.some(param => attributeNames.some(attr => param.toUpperCase().split(".").indexOf(attr.toUpperCase()) > -1))) {
                    try {
                        const params = await this._actualObject.getParameters(this._conditionParamAttrs);
                        this._npDataSource.objectList.conditionParams = params;
                        this._dataSource.conditionParams = params;
                        await this._selectAll(false); // zruší označení záznamů a provede refresh LOV
                    } catch (err) {
                        ErrorHandler.instance.handleError(err);
                    }
                }

                // doposud bylo toto ne-awaitované (je možné, že to v budoucnu bude potřeba)
                this._newEvaluateRights([RightType.visible, RightType.enabled]);

                for (const attr of attributeNames) {
                    if (StringUtils.compareCaseInsensitive(attr, this.attrName)) {
                        // Pokud je nastaveno preselection a objectlist v aktuálním objektu se změnil, tak se aktualizuje do MSLOV
                        if (this._preselection) {
                            const objList = (await this._actualObject.getValue(this.attrName)) as OWObjectList;
                            if (!this._npDataSource.objectList.compare(objList)) {
                                this._dataGrid.clearSelectedRows(false);
                                this._itemFromOl.clear();
                                this._selectedItemForCond.clear();
                                this._clearConditions();
                                await this._loadObjectList(objList);
                                await this._rebuildConditions();
                                await this._applyConditions(MSLOVConditionApply.refreshWithoutSet);
                            }
                        }
                        return;
                    }
                }
            });

            await this._newEvaluateRights([RightType.visible, RightType.enabled]);
        } else {
            this._setActualObject(null);
        }

        this._filterManager.updateFilterPanel();
        this._resolveActualObjectSetDeferred(true);
    }

    /** Načte object list do data source. */
    private async _loadObjectList(objList: OWObjectList): Promise<void> {
        let params: ConditionParams = [];

        if (this._conditionParamAttrs && this._conditionParamAttrs.length > 0) {
            try {
                params = await this._actualObject.getParameters(this._conditionParamAttrs);
                this._dataSource.conditionParams = params;
            } catch (err) {
                ErrorHandler.instance.handleError(err);
            }
        }

        if (!this._isInitialized)
            await this.initialized.waitForEventTrigger(30000); // před načtením dat je potřeba počkat na doběhnutí inicializace framu

        if (objList)
            this._npDataSource.objectList = objList as OWObjectList;
        else
            this._npDataSource.objectList = new OWObjectList(undefined);
        this._npDataSource.objectList.className = this.objectName;
        this._npDataSource.objectList.conditionParams = params;
    }

    private _setButtonsConditions(isSelectAll: boolean = false, reloadCurrentData: MSLOVConditionApply = MSLOVConditionApply.refresh): void {
        this._setPreselectionBtnCondition(isSelectAll, reloadCurrentData);

        if (isSelectAll) {
            this._dataGrid.selectAllRows();
        } else {
            this._dataGrid.clearSelectedRows(true);
            this._selectedItemForCond.clear();
        }
    }

    private _rebuildConditions(): boolean {
        let conditionsChanged = false;
        let preselect: string = this._preselectionBasicCondition;
        let noSelect: string = "";
        let showCondition: string;
        let itemsCondition: string = "";

        const fullFilterCondition = StringUtils.combineConditions(ConditionOperator.and, this._filterCondition, this._fulltextCondition?.condition ?? "");

        // nacte seznam vlozenych polozek
        this._preselectionItemsCondition = "";
        if (this._selectedItemForCond && this._selectedItemForCond.size > 0) {
            for (const item of this._selectedItemForCond) {
                this._preselectionItemsCondition += `,${item}`;
            }
            this._preselectionItemsCondition = `${this._dataSource.dataKey} in [ ${this._preselectionItemsCondition.substring(1)} ]`; //`
        }
        preselect = StringUtils.combineConditions(ConditionOperator.or, preselect, this._preselectionItemsCondition);

        // nacte seznam vyloučených polozek
        this._nopreselectionItemsCondition = "";
        if (this._removeItemFromCond && this._removeItemFromCond.size > 0) {
            for (const item of this._removeItemFromCond) {
                this._nopreselectionItemsCondition += `,${item}`;
            }
            this._nopreselectionItemsCondition = `not (${this._dataSource.dataKey} in [ ${this._nopreselectionItemsCondition.substring(1)} ])`; //`
        }

        // nacte polozky s OL a na zaklade nastaveni isSelectAll na OL provede bud vybrani a nebo nevybrani polozek
        if (!this._npDataSource.isSelectAll) {
            if (this._npDataSource.objectList && this._npDataSource.objectList.items && this._npDataSource.objectList.items.length > 0) {
                this._npDataSource.objectList.items.forEach((item) => this._itemFromOl.add(item.id));
                this._npDataSource.objectList.items = []; // vycisteni OL pred aplikaci nastaveni (vsechny items sou jiz v podmince)
            }
        } else {
            this._itemFromOl.clear();
        }

        if (this._itemFromOl.size > 0) {
            for (const item of this._itemFromOl) {
                itemsCondition += `,${item}`;
            }

            let operator = true;
            // pokud je vybrano vse v OL tak zaznamy v nem obsazene do vyberu nepatri
            if (this._npDataSource.objectList.isSelectAll) {
                operator = false;
            }
            itemsCondition = `${this._dataSource.dataKey} in [ ${itemsCondition.substring(1)} ]`; //`
            if (!operator)
                itemsCondition = "not (" + itemsCondition + ")";
        }

        // pokud je vybrano vse obsahuje itemsCondition elmenty, ktere nesmi ve vyberu byt a je treba ji tedy s preselectem "vyANDovat"
        if (this._npDataSource.objectList.isSelectAll) {
            preselect = StringUtils.combineConditions(ConditionOperator.and, preselect, itemsCondition);
        } else {
            preselect = StringUtils.combineConditions(ConditionOperator.or, preselect, itemsCondition);
        }

        preselect = StringUtils.combineConditions(ConditionOperator.and, preselect, this._nopreselectionItemsCondition); // spojeni odebrnnych polozek s podminkou

        if (preselect)
            noSelect = `NOT (${preselect})`; //`

        showCondition = preselect;
        if (this._showPreselectOnlyFiltered) {
            showCondition = StringUtils.combineConditions(ConditionOperator.and, preselect, this._filterCondition);
        }

        const condition = StringUtils.combineConditions(ConditionOperator.and, fullFilterCondition, noSelect);

        if (this._buildedPreselectCondition !== preselect) {
            this._buildedPreselectCondition = preselect;
            conditionsChanged = true;
        }
        if (this._showPreselectCondition !== showCondition) {
            this._showPreselectCondition = showCondition;
            conditionsChanged = true;
        }
        if (this._standardCondition !== condition) {
            this._standardCondition = condition;
            conditionsChanged = true;
        }

        return conditionsChanged;
    }

    private _clearConditions(): void {
        this._preselectionBasicCondition = `${this._dataSource.dataKey} = -1`; //`
        this._preselectionItemsCondition = undefined;
        this._selectedItemForCond.clear();
        this._removeItemFromCond.clear();
        this._standardCondition = `${this._dataSource.dataKey} > 0`; // `
    }

    private async _applyConditions(reloadCurrentData: MSLOVConditionApply, isSelectAll?: boolean): Promise<void> {
        try {
            UIManager.instance.showLoader();
            let cond = this._showPreselectCondition;
            if (!this._competenceCondition) {
                this._competenceCondition = await this._getCompetenceCondition();
            }
            if (this._competenceCondition != undefined && this._competenceCondition != "")
                cond = StringUtils.combineConditions(ConditionOperator.and, cond, this._competenceCondition);
            this._dataSource.setConditions(cond, this._standardCondition, this._filterCondition, this._fulltextCondition);
            this._npDataSource.objectList.condition = this._condition;
            this._npDataSource.objectList.updateCondition = this._buildedPreselectCondition;
            this._npDataSource.objectList.filterCondition = "";
            this._logger.log(this._buildedPreselectCondition);

            // Pokud se mění hodnota isSelectAll, tak v rámci setteru se volá valueChanged a proto není potřeba volat znovu v další podmínce.
            if (isSelectAll !== undefined && this._npDataSource.isSelectAll !== isSelectAll) {
                this._npDataSource.isSelectAll = isSelectAll;
            } else if (reloadCurrentData !== MSLOVConditionApply.onlySetCondition && reloadCurrentData !== MSLOVConditionApply.refreshWithoutSet) {
                this._npDataSource.valueChanged.trigger(this._npDataSource, { Sender: this._npDataSource });
            }

            if (reloadCurrentData === MSLOVConditionApply.reloadCurrentData)
                return this._dataSource.reloadData();
            else if (reloadCurrentData === MSLOVConditionApply.refresh || reloadCurrentData === MSLOVConditionApply.refreshWithoutSet) {
                if (this._autoRefreshInterval && this.menu) {
                    this.menu.setProperty(FormUICustomProperty.notificationDot, {
                        action: ActionType.standardRefreshList,
                        visibility: false,
                    });
                }
                await this._dataSource.loadNewData();
                return;
            }
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    private _setBusyState(busy: boolean): void {
        if (this._lastBussyPromise && busy || !this._lastBussyPromise && !busy) { //stav se nemění
            return;
        }
        if (busy) {
            this._lastBussyPromise = PromiseUtils.defer();
        } else {
            this._lastBussyPromise.resolve();
            this._lastBussyPromise = undefined;
        }
    }

    /**
     * Umožňuje čekat na běžící procesy na komponentě
     */
    public waitForPendingActions(): Promise<void> {
        return this._lastBussyPromise ? this._lastBussyPromise.promise : Promise.resolve();
    }

    /**
     * Umožňuje zrušit běžící procesy na komponentě
     */
    public cancelPendingActions(): void {
        throw new Error("Not implemented");
    }

    /**
     * Přegeneruje podmínky
     * Zavedeno, aby vyhovovalo interfacu IFulltextSearchableFrame. Jen zavolá jinou metodu MSLOV.
     * Vrací základní podmínku pro čtení
     */
    public override createReaderCondition(): Promise<string> {
        this._rebuildConditions();
        return Promise.resolve(StringUtils.combineConditions(ConditionOperator.and, this._standardCondition, this._condition));
    }

    protected override async _createReaderConditionParams(): Promise<ConditionParams> {
        if (this.actualObject)
            return await this.actualObject.getParameters(this._conditionParamAttrs);

        return [];
    }

    /**
     * Vygeneruje nove podminky a parametry pro cteni dat datasourcem a zahaji nacitani dat
     * Zavedeno, aby vyhovovalo interfacu IFulltextSearchableFrame. Jen zavolá jinou metodu MSLOV.
     */
    public async updateConditionAndRead(): Promise<void> {
        await this.createReaderCondition();
        return this._applyConditions(MSLOVConditionApply.refreshWithoutSet);
    }

    private async _switchShowFilterItems(val: boolean): Promise<void> {
        this._showPreselectOnlyFiltered = val;
        let cond = `(${this._buildedPreselectCondition})`;

        if (this._filterCondition) {
            //Pokud všechny vybrané záznamy odpovídají filtru není potřeba zobrazovat tlačítko pro skrytí/zobrazení záznamů neodpovídajících filtru.
            cond = StringUtils.combineConditions(ConditionOperator.and, cond, `NOT (${this._filterCondition})`); //záznam je mezi označenýma prvkama, ale zároveň není vyfiltrován.
        }

        if (!(await getDataReaderClient().exists(this._dataSource.className, cond, undefined, this.ownerForm.readVersioned))) {
            this._showFilterItemsSwitchButton(false, false);
        } else {
            this._showFilterItemsSwitchButton(val);
        }

        this._rebuildConditions();
        this._applyConditions(MSLOVConditionApply.refreshWithoutSet);
    }

    private _showFilterItemsSwitchButton(showAll: boolean, onlyFilter: boolean = undefined): void {
        this._showAllItemsActionVisible = showAll;
        this._showAllItems.visible = this._showAllItemsActionVisible;
        this._showOnlyFilterItemsActionVisible = onlyFilter == undefined ? !showAll : onlyFilter;
        this._showOnlyFilterItems.visible = this._showOnlyFilterItemsActionVisible;
    }

    private async _onSelectionChanged(evt: IDataGridSelectionChangedEvent): Promise<void> {
        let prm: Deferred<void> = null;
        try {
            let row: IDataRow;
            if (evt.target instanceof DataRow)
                row = evt.target as DataRow;
            else if (evt.detail.rowID)
                row = this._dataGrid.dataRows.find(r => r.dataKey == evt.detail.rowID);
            if (!row)
                return;

            prm = PromiseUtils.defer<void>();
            this.addPendingUpdateTask(prm.promise);

            this._setBusyState(true);

            const rowId = evt.detail.data.clickedRow ? evt.detail.data.clickedRow.dataKey.toString() : "-1";

            if (rowId == -1) {
                this._logger.warn("RowId undefined selectionChanged not called");
                return;
            }

            if (row.isSelected) {
                this._valueMap.set(rowId.toString(), { id: rowId, delete: false });
                this._removeItemFromCond.delete(rowId.toString());
            } else {
                this._valueMap.set(rowId.toString(), { id: rowId, delete: true });
                this._removeItemFromCond.add(rowId.toString());
            }
        } finally {
            this._executeQueue().then(() => {
                if (prm)
                    prm.resolve();
            });
        }
    }
    private async _onMultipleSelectionChanged(evt: IDataGridMultipleSelectionChangedEvent): Promise<void> {
        let prm: Deferred<void> = null;
        try {
            prm = PromiseUtils.defer<void>();
            this.addPendingUpdateTask(prm.promise);

            this._setBusyState(true);

            const changedRows = this._dataGrid.dataRows.filter(r => evt.detail.rowIDs.indexOf(r.dataKey) >= 0);

            if (this._npDataSource.isSelectAll) {
                // pokud ma OL nastaven isSelectAll, tak pokud je radek vybran je vybran mimo puvodni podminku a je pridan k vyberu,
                // pokud neni vybran, je pridan do OL, protoze v OL, kdyz je isSelectAll tak objekty v nem jsou ty, ktere vybrany byt nemaji
                changedRows.forEach(row => {
                    this._valueMap.set(row.dataKey, { id: row.dataKey, delete: !row.isSelected });
                    if (row.isSelected)
                        this._removeItemFromCond.delete(row.dataKey.toString());
                    else
                        this._removeItemFromCond.add(row.dataKey.toString());
                });
            } else {
                changedRows.forEach(row => {
                    this._valueMap.set(row.dataKey, { id: row.dataKey, delete: !row.isSelected });
                });
            }
        } finally {
            this._executeQueue().then(() => {
                if (prm)
                    prm.resolve();
            });
        }
    }

    private _clearQueue(): void {
        this._valueMap.clear();
        this._valueMapCanAddingItem = false;
        //this._NPDataSource.clearObjectList();
    }

    /**
     * Executes the queue of tasks.
     * @param delay - the delay after which the queue will be executed.
     */
    private _executeQueue(delay: number = 1500): Promise<void> {
        if (delay <= 0) {
            this._queueFnc();
            this._lastQueueTask = Promise.resolve();
        } else {
            this._lastQueueTask = TimerUtils.waitOrCancel(this._queueIdName, () => this._queueFnc(), delay);
        }
        return this._lastQueueTask;
    }

    private _queueFnc(): void {
        const q = this._valueMap;
        this._valueMapCanAddingItem = true;
        this._valueMap = new Map<string, { id: string; delete: boolean }>();

        for (const item of q.values()) {
            if (!this._valueMapCanAddingItem)
                break;

            //if (item.delete && !this._NPDataSource.isSelectAll || !item.delete && this._NPDataSource.isSelectAll) await this._NPDataSource.deleteObjectById([item.id]);
            if (item.delete) {
                this._itemFromOl.delete(item.id.toString());
            } else {
                this._itemFromOl.add(item.id.toString());
            }
        }
        const conditionsChanged = this._rebuildConditions();
        if (this._firstQueueRun || conditionsChanged) {
            this._applyConditions(MSLOVConditionApply.setWithoutRefresh);
            this._firstQueueRun = false;
        }

        if (this._valueMap.size === 0) {
            this._setBusyState(false);
        }
    }

    /**
     * Nacteni uvodnich podmínek pro preselection nebo defaultSelectionState
     */
    private async _setInitConditionSetting(): Promise<void> {
        this._clearConditions();

        if (this._defaultSelectionState) {
            this._npDataSource.isSelectAll = true;
            this._preselectionBasicCondition = `${this._dataSource.dataKey} > 0`; //`
            this._standardCondition = `${this._dataSource.dataKey} = -1`; // `
            //this._dataGrid.SelectAllRows();
        } else if (!this._preselection) {
            this._npDataSource.clearObjectList();
        }

        this._rebuildConditions();
        await this._applyConditions(MSLOVConditionApply.onlySetCondition); // onlySetCondition = při inicializaci řeší načtení dat volající metoda (mimo jiné i kvůli QueryMode)
    }

    protected _createDesktopDropDownMenu(): void {
        this._headerDropDown = new DropDownMenu();

        if (!this._hideFilterRow)
            this._headerDropDownFilterItem = FrameSharedMethods.createColumnDropDownMenuFiltering(this._headerDropDown, this.frameContext);

        if (!this._disableSort)
            FrameSharedMethods.createColumnDropDownMenuOrdering(this._headerDropDown, this._dataGrid, this.objectName, () => this._headerDropDownHc, async (newOrdering: IOrderBy[]) => {
                await this._changeOrdering({ newOrdering });
            });
    }

    /**
     * Vytvori seznam sloupcu
     * @param element - html definice seznamu
     */
    protected override async _createColumns(columnDefinitions: IColumnDefinition[]): Promise<any> {
        const columns = new Array<Column>();
        let attrs = new Array<string>();

        try {
            const attrMap = await FrameSharedMethods.mapColumnDefinitionsToAttributeInfos(this.objectName, columnDefinitions.filter(columnDefinition => columnDefinition.visible));

            for (const attr of attrMap) {
                const column: Column = await ColumnFactory.initColumn(attr.columnDefinition, attr.attributeInfo.type, attr.attributeInfo, undefined, this._disableSort);

                if (!column.visible)
                    continue;

                if (column.canSort) {
                    this._events.addListener(column, "HeaderClickEvent", async (event: CustomEvent) => {
                        await this._changeOrdering({ event, column });
                    });
                }

                this._events.addListener(column, "dropdownmenushow", (e: CustomEvent) => {
                    const column = e.target as Column;
                    // atribut pro LOV musi obssahovat i vazbu // nastaveni attributu dropdown menu
                    if (this._headerDropDownFilterItem)
                        this._headerDropDownFilterItem.attrName = column.attrNames[0];
                    if (column !== this._headerDropDownHc || !this._headerDropDown.visible) {
                        if (this._filterManager.currentFilter == null) {
                            const fblock = this._filterManager.createNew(false);
                            this._filterManager.selectFilter(fblock);
                            this._filterManager.updateFilterPanel();
                        } else if (this._filterManager.filterPanel.currentFilterBlock !== this._filterManager.currentFilter) {
                            /* Tato větev je pro případ, kdy uživatel používá filtry pro více MSLOV na jednom formuláři a přechází mezi nimi pomocí kliknutí na hlavičku v DataGridu.
                            Bez této větve se nepřekreslí obsah filtr panelu a dojde ke ztrátě vazby FiltrBlocku na MSLOV a tím pádem nedojde k filtrování. */
                            this._filterManager.updateFilterPanel();
                        }
                        this._headerDropDown.show(e.detail.dropdownelemnt, e, e.detail.originalEvent.detail === 0); // detail === 0 je v eventu nastaven, pokud přijde z klávesnice
                        this._headerDropDownHc = e.target as Column; //e.detail.column;
                        if (this._headerDropDownFilterItem)
                            this._headerDropDownFilterItem.focus();

                        this._headerDropDown.getElementsByClassName("NoSortMenuItem")[0]?.classList.toggle("hidden", column.canSort);
                    }
                });

                this._events.addListener(column, "visibility", (e: CustomEvent) => {
                    if (!e.detail.value) {
                        this._headerDropDownHc.headerCell.visibility = false;

                        for (const row of this._dataGrid.dataRows) {
                            row.removeCellByIndex(this._headerDropDownHc.order);
                        }
                    }

                    this._dataGrid.generateColumnsOrder();
                });

                columns.push(column);
                attrs = attrs.concat(column.dataSourceAttrNames);

                this._dataGrid.appendChild(column);
            }

            if (this.fontColorAttr && this.fontColorAttr.length > 0) {
                attrs.push(this.fontColorAttr);
            }

            this._dataGrid.columns = columns;
            const fullLengthAttributes = columns.filter(column => column.trimValue === false).map(column => column.attrName);

            this._dataSource.setAttrs(attrs, fullLengthAttributes);
        } catch (err) {
            this._logger.error(`Unable to load column attribute info for ${JSON.stringify(columnDefinitions.map(column => column.attrName))}`); //``
            throw err;
        }
    }

    /**
     * Nacte filtry pro mslov
     */
    private async _loadFilters(): Promise<void> {
        const columns = await FilterManager.getColumnListByDataGridColumns(this._dataGrid.columns, this._dataSource.className);
        this._filterManager = new FilterManager({
            filterPanel: this.ownerForm.filterPanel,
            columns,
            formName: this.ownerForm.formName,
            frameUniqueIdentifier: this.uniqueIAttrIdentifier,
            className: this._dataSource.className,
            frameCondition: await this.createReaderCondition(),
            frameConditionParams: await this._createReaderConditionParams(),
            currentUserId: AppContext.instance.currentUser?.id,
            frameContext: this.frameContext,
            disableValueShortening: false,
            canEditImplementationFilters: await DIContainer.instance.create(ISystemOperationRights).get(SystemOperation.EDITIMPPROFILE),
        });

        await this._filterManager.init();

        this._events.add(this._filterManager.filterChanged, async (_fm, fb) => {
            if (this._headerDropDownFilterItem && fb.bindingFilterBlock)
                this._headerDropDownFilterItem.filterBlock = fb.bindingFilterBlock;

            if (!this._hideFilterRow) { // pokud je hideFilterRow=true, nezobrazuje se toolbarButton, takže není potřeba nastavovat jeho Caption
                //this._filterListAction.toolbarButton.setCaption(fb.filterName);
                this._setFilterToolbarCaption(fb.filterName);
            }
        });

        this._events.add(this._filterManager.filterCleared, () => {
            this._showFilterItemsSwitchButton(false, false);
        });

        this._events.add(this._filterManager.filterExecuted, async (_s, filterEventArgs) => {
            if (this._showFilterValues) {
                this._dataGrid.setFilters(filterEventArgs.filter);
            }

            this._filterManager.updateFilterPanel();
            this.ownerForm.filterPanel.color = this.headerColor;
            if (!this._showFilterValues)
                this.ownerForm.filterPanel.show();

            if (this._lastFilterCTS)
                this._lastFilterCTS.cancel();

            this._lastFilterCTS = new CancellationTokenSource();

            this._filterCondition = filterEventArgs.condition;
            this._npDataSource.objectList.filterCondition = filterEventArgs.condition;
            this._npDataSource.isSelectAll = false;
            this._dataSource.setFilterCondition(filterEventArgs.condition);
            this._rebuildConditions();
            this._applyConditions(MSLOVConditionApply.refreshWithoutSet);

            this._dataSource.loadNewData(false, this._lastFilterCTS.token).then(() => {
                let filterActive = false;
                for (const col of this._dataGrid.columns) {
                    if (filterEventArgs.columns && filterEventArgs.columns.get(col.attrName)) {
                        col.isFiltered = true;
                        filterActive = true;
                    } else
                        col.isFiltered = false;
                }

                if (filterActive || this._filterManager.hasActiveAdvancedFilter) {
                    this._filterAction.toolbarButton.setIcon(Icon.filterFilled);

                    this._switchShowFilterItems(false);
                } else {
                    this._filterAction.toolbarButton.setIcon(Icon.filter);

                    this._showFilterItemsSwitchButton(false, false);
                }

                this.element.classList.toggle("filter-on", filterActive);
            }).catch(PromiseUtils.cancellationCatch);
        });

        this._events.add(this._filterManager.filterSaveStateChanged, async (_s, fb) => {
            if (this._filterManager.getCurrentFilter()?.filterId === fb.filterId)
                this._filterListAction.toolbarButton.setCaption(await this._filterManager.getFilterCaption(fb));
        });

        this._events.add(this._filterManager.filterCleared, (_s, v) => {
            this._filterAction.toolbarButton.setIcon(Icon.filter);
            this._setFilterToolbarCaption();
            if (this._showFilterValues) this._dataGrid.setFilters(null);

            this._filterCondition = v;
            this._npDataSource.objectList.filterCondition = v;
            this._npDataSource.isSelectAll = false;

            this._rebuildConditions();
            this._applyConditions(MSLOVConditionApply.refreshWithoutSet);

            for (const col of this._dataGrid.columns) {
                col.isFiltered = false;
            }
        });
    }

    private async _createFilterBtnMenu(): Promise<void> {
        const insertBtn = DropDownMenuItem.createDropMenuItem("insert",
            async () => {
                if (!this.ownerForm.filterPanel.isShown()) {
                    this._showFilterPanel();
                }
                this._filterManager.insertFilter();
            }, "GUI-674376", true); // Vložit filtr

        const advancedFilterBtn = DropDownMenuItem.createDropMenuItem("filter", async () => {
            this._showFilterPanel();
            this._filterManager.showAdvancedFilter();
        }, "GUI-691373", true); // Pokročilé filtrování

        const emptyBtn = DropDownMenuItem.createDropMenuItem("checked",
            async () => {
                this._filterManager.clearFilter();
                emptyBtn.icon = "checked";
                this._setFilterToolbarCaption();
            },
            "GUI-670216", //Vše
            true);

        const filterClearedKey = "mslovFilterCleared";
        this._events.removeByKey(filterClearedKey);
        this._events.addWithKey(filterClearedKey, this._filterManager.filterCleared, () => {
            if (this._showFilterValues) this._dataGrid.setFilters(null);
            emptyBtn.icon = "checked";
        });

        const filterMap = await this._filterManager.getFilterList();

        // vytvořené menu se nesmí smazat, jinak se uživateli do otevřeného menu nepromítnou nové položky
        if (this._filterMenu == null) {
            this._filterMenu = new DropDownMenu();
            this._filterMenu.horizontalOpenPosition = DropDownMenuHorizontalOpen.right;
        } else {
            this._filterMenu.clearItems();
        }

        this._filterMenu.addItem(insertBtn); // Vložit
        this._filterMenu.addItem(advancedFilterBtn); // Pokročilé filtrování
        this._filterMenu.addItem(new SeparatorMenuItem());
        this._filterMenu.addItem(emptyBtn); // Žádný

        const dropDownItemsPromiseArr: Promise<DropDownMenuItem>[] = [];

        for (const filterInfo of filterMap) {
            dropDownItemsPromiseArr.push(this._filterManager.createFilterMenuItem(filterInfo[1], emptyBtn));
        }

        const resolvedItemArray = await Promise.all(dropDownItemsPromiseArr);

        for (const dropDownItem of resolvedItemArray) {
            this._filterMenu.addItem(dropDownItem);
        }
    }

    private async _loadPreselectionSetting(): Promise<void> {
        await this._setInitConditionSetting();

        if (!this._preselection && this._npDataSource.objectList.isSelectAll && !this._defaultSelectionState) {
            //pokud bylo pri poslednim otevreni neco vybrano je zapotrebi na sluzbe prenastavit
            this._npDataSource.objectList.isSelectAll = false;
            this._npDataSource.valueChanged.trigger(this._npDataSource, { Sender: this._npDataSource });
        } else if (this._npDataSource.objectList.isSelectAll) {
            this._npDataSource.valueChanged.trigger(this._npDataSource, { Sender: this._npDataSource });
        }

        if (this.masterFrame || !this.ownerForm.queryMode) {
            await this._dataSource.loadNewData();
        }
    }

    private _setPreselectionBtnCondition(isSelectAll: boolean, reloadCurrentData: MSLOVConditionApply): void {
        if (isSelectAll) {
            // pokud neni vyplnena filterCondition nebo sou zobrazeny vsechny zaznamy, tak vybirame cely seznam a vycistime ostatni seznamy
            if (StringUtils.isNullOrWhiteSpace(this._filterCondition)) {
                this._clearConditions();
                this._npDataSource.clearObjectList();
                this._preselectionBasicCondition = `${this._dataSource.dataKey} > 0`; // `
            } else {
                // pokud jsou zobrazeny vsechny vybrane polozky, je treba filtr neprepsat, ale pridat
                this._preselectionBasicCondition = StringUtils.combineConditions(ConditionOperator.or, this._preselectionBasicCondition, this._filterCondition);

                // pokud pri vyberu vse se v OL nachazi nejaky polozky je treba je presunout do podminky, protoze by v OL na sluzbe i u nas byly brany jako neoznacene
                if (this._npDataSource.objectList.items.length > 0) {
                    for (const item of this._npDataSource.objectList.items)
                        this._selectedItemForCond.add(item.id.toString());
                    this._npDataSource.clearObjectList();
                }

                if (this._itemFromOl.size > 0) {
                    for (const item of this._itemFromOl)
                        this._selectedItemForCond.add(item.toString());
                }
                this._itemFromOl.clear();
            }
        } else {
            this._dataSource.selectChangeOnData(false, false);
            this._npDataSource.clearObjectList();
            this._itemFromOl.clear();
            this._clearConditions();
        }
        this._rebuildConditions();
        this._applyConditions(reloadCurrentData, isSelectAll);
    }

    /**
     * Vyčistí nastavení profilu a vrátí seznam do původního stavu.
     */
    public override clearProfileInfo(): void {
        if (this._dataGrid.initialized) {
            this._clearProfileInfo();
            this.selectedProfileChanged.trigger(this, this.profileInfo);
        } else {
            this._events.add(this.dataGridInitialized, () => {
                this._clearProfileInfo();
            }, { oneTimeOnly: true });
        }
    }

    private async _clearProfileInfo(): Promise<void> {
        if (!this.currentDataGrid || !this.currentDataGrid.canValueLoad)
            return;

        // Nepoužívat zde setter předka ani této třídy, způsobuje vícenásobné volání selectedProfileChanged, protože je tato metoda volána právě i ze setteru.
        this._profileInfo = { columns: {}, order: this._definitionOrderBy };
        const attrs = Array.from(this.element.querySelectorAll("[data-behaviour='column']")) as HTMLElement[];

        for (const [index, attr] of attrs.entries()) {
            const attrName = attr.getAttribute("data-attrname");
            const width = DataConverter.toNumber(attr.getAttribute("data-width"));
            const visibility = DataConverter.toBoolean(attr.getAttribute("data-visible"), true);
            const column = this._dataGrid.columns.find((item) => StringUtils.compareCaseInsensitive(item.attrName, attrName));

            if (column) {
                // Nastaví se podle pozice sloupce na seznamu, počítá se od 1 kvůli profilům.
                this._dataGrid.setColumnOrder(column, index + 1);
                this._dataGrid.setColumnWidth(column, width);
                column.visible = visibility;
            }
        }
        const orderByChanged = this._dataGrid.setDefaultOrderBy(this._definitionOrderBy);
        if (orderByChanged)
            this.orderBy = this._dataGrid.orderBy;
    }

    public override deleteRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override editRecord(): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override newRecord(_objectName?: string): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    /**
     * Navratove hodnoty:
     * null - ulozeni probehlo v poradku
     * prazdne pole - ulozeni se nepodarilo, ale neni co zpracovat
     * naplene pole - vraci vyjimky, vznikle pri ukladani
     * @param id Pokud je vyplněn, uloží pouze jeden záznam. V opačném případě ukládá všechny rozeditované objekty.
     */
    public override async saveRecords(id?: string, checkRecommended?: boolean, _checkPostponed?: boolean, _skipValidation?: boolean, _isFormClosing?: boolean, skipSavingEditContextId?: boolean): Promise<SaveResultData[]> {
        await super.saveRecords();
        // pred ukladanim je potreba pockat na mozne probihajici updaty
        await this.waitForPendingUpdates();

        // stav před začátkem ukládání - pokud už byl seznam v readonly, tak jeho stav neměníme (třeba když je z něj otevřený modální detailový formulář, který spouští uložení)
        // jinak se přepne do readonly jen na chvíli, než doběhne ukládání záznamů
        try {
            let clientValidationFail = false;
            if (!(await this.validate(checkRecommended))) {
                clientValidationFail = true;
            }

            UIManager.instance.showLoader();

            const resultMessages = await this._dataSource.saveObjects(id ? [id + ""] : undefined); //+"" protože se objekt předával jako number a nešel porovnat pomocí indexOf
            if (this.isEditContextInitiator && !skipSavingEditContextId) {
                await this.saveContext();
            }

            const savingResult = await SharedMethods.processSaveResultsAndShowMessages(this, resultMessages);

            if ((await SharedMethods.checkObjectBeforeExecute(this.validationManager, "GUI-626119", "GUI-33412")) == ObjectCheckingResult.recommendedContinue) { //"GUI-626119"=Pokračovat,"GUI-33412"=Zrušit)
                this.validationManager.clearErrors();
                return await this.saveRecords(id, false);
            }

            if ((savingResult != undefined) && savingResult.length > 0) {
                this._dataGrid.selectRow(savingResult[0].data.id);
                return savingResult;
            }

            if (clientValidationFail || this._dataSource.editedObjectsCount > 0)
                return savingResult;

            this.isEditing = false;

            await this._doMasterRefresh();
            this.actualObjectSaved.trigger(this, this.actualObject);

            return null;
        } finally {
            UIManager.instance.hideLoader();
        }
    }

    public override async cancelChanges(): Promise<void> {
        if (this.isEditContextInitiator) {
            await this.rollbackContext();
        }
        this.setToolbarToEditMode(false);
        await this._dataSource.cancelChanges();
        this.validationManager.clearErrors();
        this.isChanged = false;
        this.changesCanceled.trigger(this, undefined);
        if (this._dataGrid) {
            let obj: OWObject = null;
            if (this._dataGrid.selectedRows.length === 0 && this._dataGrid.lastClickedRow == undefined) {
                this._dataGrid.clearSelectedRows();
                this._selectedObjectForDetailForm = null;
            } else {
                obj = await this._dataSource.getObject(
                    this._dataGrid.lastClickedRow ? this._dataGrid.lastClickedRow.dataKey : this._dataGrid.selectedRows[0].dataKey,
                    await FrameSharedMethods.createAfterCreateExtendedOperations(this._createExtendedOperationsParams(), false),
                    true,
                );
                this._selectedObjectForDetailForm = obj;
                const stringId = obj.id.toString();
                this._dataGrid.selectRow(stringId);
            }
            this.selectionChanged.trigger(this, { sender: this, objectName: obj == null ? "" : this.objectName, actualObject: obj });
        }
    }

    public override prevRow(): void {
        throw new Error("Method not implemented.");
    }

    public override nextRow(): void {
        throw new Error("Method not implemented.");
    }

    public override createCopy(_propagateError?: boolean): Promise<boolean> {
        throw new Error("Method not implemented.");
    }

    public override async refreshOnClick(): Promise<void> {
        this.menu.setProperty(FormUICustomProperty.notificationDot, {
            action: ActionType.standardRefreshList,
            visibility: false,
        });
        await this._dataSource.loadNewData();
        this._applyConditions(MSLOVConditionApply.setWithoutRefresh);
        this._dataGrid.dataRows;
    }

    public loadProfile(filterId: string, queryMode: boolean): void {
        if (queryMode) {
            if (filterId != null) {
                this.filterManager.selectFilterById(filterId, true);
            }
        } else {
            this.filterManager.selectAndApplyFilterById(filterId, true);
        }
    }

    protected override async _onMasterObjectChanged(_masterObjectChangedArgs?: IActualObjectChangedEventArgs): Promise<void> {
        // do nothing.
    }

    public override dispose(): void {
        super.dispose();

        this._dataSource?.dispose();
        this._dataGrid?.dispose();
        this.__rightsEvaluationOverrides?.dispose();

        this._headerDropDown?.dispose();
        this._headerDropDownFilterItem?.dispose();
        this._headerDropDownHc?.dispose();

        this._filterManager?.dispose();
        this._filterMenu?.dispose();
        this._filterAction?.dispose();
        this._filterListAction?.dispose();

        this._showAllItems?.dispose();
        this._showOnlyFilterItems?.dispose();
        this._selectItemAction?.dispose();
        this._selectNoItemAction?.dispose();
        this._refreshOnClick?.dispose();
    }

    /** Vrací objekt, který obsahuje informace o prvku */
    public async getControlInfo(childInfo: Partial<IControlInfoObject>, clickedControl: IGenericControl): Promise<Partial<IControlInfoObject>> {
        return FrameSharedMethods.getControlInfo(this, childInfo, clickedControl);
    }
}

enum MSLOVConditionApply {
    onlySetCondition,
    refresh,
    refreshWithoutSet,
    reloadCurrentData,
    setWithoutRefresh,
}
